import React from 'react';
import { Helmet } from 'react-helmet';
import NewtonsSecondLawCalculator from '../calcualtors/Physics/NewtonsSecondLawCalculator';
import ProjectileMotionCalculator from '../calcualtors/Physics/ProjectileMotionCalculator';
import SimplePendulumCalculator from '../calcualtors/Physics/SimplePendulumCalculator';
import KineticEnergyCalculator from '../calcualtors/Physics/KineticEnergyCalculator';
import PotentialEnergyCalculator from '../calcualtors/Physics/PotentialEnergyCalculator';
import WorkCalculator from '../calcualtors/Physics/WorkCalculator';
import IdealGasLawCalculator from '../calcualtors/Physics/IdealGasLawCalculator';
import HeatTransferCalculator from '../calcualtors/Physics/HeatTransferCalculator';
import ThermalExpansionCalculator from '../calcualtors/Physics/ThermalExpansionCalculator';
import SpecificHeatCalculator from '../calcualtors/Physics/SpecificHeatCalculator';
import EntropyCalculator from '../calcualtors/Physics/EntropyCalculator';
import ThermalConductivityCalculator from '../calcualtors/Physics/ThermalConductivityCalculator';
import EinsteinMassEnergyCalculator from '../calcualtors/Physics/EinsteinMassEnergyCalculator';
import DeBroglieWavelengthCalculator from '../calcualtors/Physics/DeBroglieWavelengthCalculator';

const PhysicsCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Physics Calculators | Comprehensive Physics Tools</title>
        <meta name="description" content="Collection of 281+ physics calculators for solving mechanics, thermodynamics, electricity, optics, and modern physics problems." />
        <meta name="keywords" content="physics calculators, mechanics calculators, thermodynamics calculators, electricity calculators, optics calculators, quantum physics calculators" />
        <meta property="og:title" content="Physics Calculators | Comprehensive Physics Tools" />
        <meta property="og:description" content="Collection of 281+ physics calculators for solving mechanics, thermodynamics, electricity, optics, and modern physics problems." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Physics Calculators | Comprehensive Physics Tools" />
        <meta name="twitter:description" content="Collection of 281+ physics calculators for solving mechanics, thermodynamics, electricity, optics, and modern physics problems." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Physics%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">⚛️</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Physics Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">281 calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Explore our extensive collection of physics calculators covering mechanics, thermodynamics, electricity, optics, and modern physics. These tools help students, educators, and professionals solve complex physics problems quickly and accurately.
            </p>
            <button className="read-more inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Mechanics <span className="emoji ml-2">⚙️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/physics/newtons-second-law" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Newton's Second Law Calculator</h3>
                <p className="text-gray-600">Calculate force using F=ma</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/projectile-motion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Projectile Motion Calculator</h3>
                <p className="text-gray-600">Calculate projectile trajectories</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/simple-pendulum" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Simple Pendulum Calculator</h3>
                <p className="text-gray-600">Calculate period and frequency</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/kinetic-energy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kinetic Energy Calculator</h3>
                <p className="text-gray-600">Calculate energy of moving objects</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/potential-energy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Potential Energy Calculator</h3>
                <p className="text-gray-600">Calculate gravitational potential energy</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/work-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Work Calculator</h3>
                <p className="text-gray-600">Calculate work done by force</p>
              </a>
            </div>
          
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Thermodynamics <span className="emoji ml-2">🔥</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/ideal-gas-law" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ideal Gas Law Calculator</h3>
                <p className="text-gray-600">Calculate gas properties using PV = nRT</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/heat-transfer" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Heat Transfer Calculator</h3>
                <p className="text-gray-600">Calculate heat transfer rates using Fourier's Law</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/thermal-expansion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Thermal Expansion Calculator</h3>
                <p className="text-gray-600">Calculate expansion of materials</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/specific-heat" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Specific Heat Calculator</h3>
                <p className="text-gray-600">Calculate heat capacity using Q = mcΔT</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/entropy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Entropy Calculator</h3>
                <p className="text-gray-600">Calculate entropy changes using ΔS = Q/T</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/thermal-conductivity" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Thermal Conductivity Calculator</h3>
                <p className="text-gray-600">Calculate thermal conductivity and heat transfer rates</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Electricity & Magnetism <span className="emoji ml-2">⚡</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/ohms-law" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ohm's Law Calculator</h3>
                <p className="text-gray-600">Calculate voltage, current, and resistance</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/power-calculator" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Power Calculator</h3>
                <p className="text-gray-600">Calculate electrical power</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/resistors-in-series" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Resistors in Series Calculator</h3>
                <p className="text-gray-600">Calculate total resistance</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/capacitors-in-parallel" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Capacitors in Parallel Calculator</h3>
                <p className="text-gray-600">Calculate total capacitance</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/magnetic-force" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Magnetic Force Calculator</h3>
                <p className="text-gray-600">Calculate magnetic forces on charges</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/electric-field" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Electric Field Calculator</h3>
                <p className="text-gray-600">Calculate electric field strength</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Optics <span className="emoji ml-2">🔍</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/lens-maker-equation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Lens Maker Equation Calculator</h3>
                <p className="text-gray-600">Calculate focal length of lenses</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mirror-equation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mirror Equation Calculator</h3>
                <p className="text-gray-600">Calculate image formation by mirrors</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/snells-law" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Snell's Law Calculator</h3>
                <p className="text-gray-600">Calculate refraction angles</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/doppler-effect" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Doppler Effect Calculator</h3>
                <p className="text-gray-600">Calculate frequency shift</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/wavelength" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Wavelength Calculator</h3>
                <p className="text-gray-600">Calculate wavelength from frequency</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/photon-energy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Photon Energy Calculator</h3>
                <p className="text-gray-600">Calculate energy of light particles</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Modern Physics <span className="emoji ml-2">⚛️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/einstein-mass-energy" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Einstein Mass-Energy Calculator</h3>
                <p className="text-gray-600">Calculate energy using E=mc²</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/de-broglie-wavelength" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">de Broglie Wavelength Calculator</h3>
                <p className="text-gray-600">Calculate matter wave wavelength</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/quantum-numbers" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quantum Numbers Calculator</h3>
                <p className="text-gray-600">Determine quantum states</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/schrodinger-equation" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Schrödinger Equation Calculator</h3>
                <p className="text-gray-600">Solve quantum mechanical problems</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/relativistic-doppler-effect" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Relativistic Doppler Effect Calculator</h3>
                <p className="text-gray-600">Calculate relativistic frequency shifts</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/half-life" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Half-Life Calculator</h3>
                <p className="text-gray-600">Calculate radioactive decay rates</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicsCalculators;